/**
 * Converter for single server configuration format
 *
 * Handles inputs like:
 * {
 *   "command": "npx",
 *   "args": ["@playwright/mcp@latest"]
 * }
 *
 * Or:
 * {
 *   "url": "https://mcp.example.com/sse",
 *   "headers": { "Authorization": "Bearer token" }
 * }
 *
 * Note: This format doesn't include a server name, so a default is used
 * which should be overridden by the user in the UI.
 */

import type { ConverterEntry, McpServerConfig, ParsedMcpServer } from '../types';
import { isValidServerConfig, normalizeConfig } from './utils';

/**
 * Extracts a reasonable default name from the config
 */
const extractDefaultName = (config: McpServerConfig): string => {
  // Try to extract name from command args (common pattern: @org/server-name)
  if (config.args && config.args.length > 0) {
    const match = config.args
      .map((arg) => arg.match(/@[\w-]+\/([\w-]+)/))
      .find((m): m is RegExpMatchArray => m !== null);
    if (match) {
      // Extract the last part, remove common prefixes/suffixes
      let name = match[1];
      name = name.replace(/^(server-|mcp-)/, '').replace(/(-server|-mcp)$/, '');
      return name || 'server';
    }
  }

  // Try to extract from URL
  if (config.url) {
    try {
      const url = new URL(config.url);
      // Use hostname without TLD
      const parts = url.hostname.split('.');
      if (parts.length > 1) {
        return parts[parts.length - 2]; // e.g., "context7" from "mcp.context7.com"
      }
      return parts[0];
    } catch {
      // Invalid URL, ignore
    }
  }

  // Fallback
  return 'mcp-server';
};

/**
 * Detects if input is a single server configuration
 */
const detect = (input: unknown): boolean => {
  if (typeof input !== 'object' || input === null) {
    return false;
  }

  // Must be a valid server config directly
  if (!isValidServerConfig(input)) {
    return false;
  }

  // Should NOT have mcpServers key (that's wrapped format)
  const obj = input as Record<string, unknown>;
  if ('mcpServers' in obj) {
    return false;
  }

  return true;
};

/**
 * Converts single server format to ParsedMcpServer array
 */
const convert = (input: unknown): ParsedMcpServer[] => {
  const config = normalizeConfig(input as McpServerConfig);
  const name = extractDefaultName(config);

  return [
    {
      name,
      config,
    },
  ];
};

export const singleServerConverter: ConverterEntry = {
  format: 'single-server',
  detect,
  convert,
  priority: 50, // Medium priority
};
